import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { isSameBoard } from "../boardNames";
import { useTranslation } from "react-i18next";
import { Send } from "lucide-react";
import { ANALYTICS_EVENTS, trackEvent } from "utils/analytics";
import { shortBoardLabel } from "../../utils/fetchCableTakeoffPaths";
import { useCablesChat } from "../useCablesChat";
import type { KarlAskAboutSource } from "../useCablesChat";
import { KarlAskQueue } from "./KarlAskQueue";
import { KarlWizardCard } from "./KarlWizardCard";
import { KarlWallsReview } from "./KarlWallsReview";
import { karlWizardSteps, nextOpenStep } from "../karlWizard";
import { useKarlAskQueue } from "../karlAskQueue";
import { INSTALLATION_HEIGHT_KEYS } from "../installationHeights";
import { MAIN_BOARD_ASK_ID } from "../karlAsks/buildBoardAsks";
import type { CablesChatPanelProps } from "./types";

// A deliberate beat, so the card reads as Karl following up rather than the
// app flashing a form at the same instant as his reply.
const CARD_DELAY_MS = 700;

const KarlMark = () => (
  <span className="flex h-6 w-6 flex-none select-none items-center justify-center rounded-md bg-gradient-to-br from-blue-600 to-blue-500 text-[12px] font-bold text-white shadow-sm">
    K
  </span>
);

const Thinking = ({ label }: { label: string }) => (
  <div className="flex items-center gap-2 py-1.5 text-[12px] text-slate-400">
    <KarlMark />
    <span className="flex items-center gap-1">
      {[0, 200, 400].map((delay) => (
        <span
          key={delay}
          className="karl-dot h-1.5 w-1.5 rounded-full bg-slate-400"
          style={{ animationDelay: `${delay}ms` }}
        />
      ))}
    </span>
    <span className="italic">{label}</span>
  </div>
);

const WALKTHROUGH_DONE = "walkthroughDone";

export const CablesChatPanel = ({
  projectId,
  planName,
  gaps,
  generatedAt,
  steps,
  sourceEdit,
  plan,
  diagram,
  onFocusCircuit,
  onProvideReset,
  onProvideNewChat,
  onProvideAskAbout,
  onBoardUnderReview,
  isActive = true,
  liveTakeoff,
  isSetupDone,
  openingFacts,
  walls,
}: CablesChatPanelProps) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState("");
  // Filled in below, once the queues exist: a new greeting starts every step's questions over.
  const restartQueuesRef = useRef<(() => void) | null>(null);
  // A new run is a new greeting; once he has worked the steps it is not a new questionnaire.
  // The stored flag, not isSetupDone: that one reads false while trays and walls are still loading.
  const hasWorkedStepsRef = useRef(Boolean(sourceEdit?.boardsConfirmed));
  hasWorkedStepsRef.current = Boolean(sourceEdit?.boardsConfirmed);
  const { messages, isSending, send, report, addCard, reset } = useCablesChat({
    projectId,
    planName,
    gaps,
    generatedAt,
    onFocusCircuit,
    isActive,
    liveTakeoff,
    isSetupDone,
    openingFacts,
    onFreshThread: () => {
      if (!hasWorkedStepsRef.current) restartQueuesRef.current?.();
    },
  });

  // Lives here rather than inside the card: the card unmounts every time the
  // group closes, and state that dies with it is every question asked again.
  const boardsQueue = useKarlAskQueue({
    steps,
    sourceEdit,
    group: "sources",
    projectId,
    planName,
    t,
  });
  const heightsQueue = useKarlAskQueue({
    steps,
    sourceEdit,
    group: "heights",
    projectId,
    planName,
    t,
  });
  const traysQueue = useKarlAskQueue({
    steps,
    sourceEdit,
    group: "trays",
    projectId,
    planName,
    t,
  });

  // Every board the plan draws, including the ones the run never routed from.
  const boardTotal = useMemo(
    () =>
      steps.sources.length +
      steps.agent.decisions.filter(
        (decision) =>
          !steps.sources.some((source) =>
            isSameBoard({ board: decision.board, name: source.name, label: source.label }),
          ),
      ).length,
    [steps.sources, steps.agent.decisions],
  );
  const removed = sourceEdit?.removed;
  const added = sourceEdit?.added;
  const renamed = sourceEdit?.names;
  const workingBoards = useMemo(() => {
    const isRemoved = (key: string) => Boolean(removed?.[key]);
    const shown = (label: string, name: string) => renamed?.[label] || name;
    const fromRun = steps.sources
      .filter((source) => !isRemoved(source.label || source.name))
      .map((source) => shown(source.label, source.name));
    const drawnOnly = steps.agent.decisions
      .filter(
        (decision) =>
          !isRemoved(decision.board) &&
          !steps.sources.some((source) =>
            isSameBoard({ board: decision.board, name: source.name, label: source.label }),
          ),
      )
      .map((decision) => decision.board);
    const byHand = Object.entries(added ?? {})
      .filter(([label]) => !isRemoved(label))
      .map(([label, board]) => shown(label, board.name));
    return { names: [...fromRun, ...drawnOnly, ...byHand] };
  }, [steps.sources, steps.agent.decisions, removed, added, renamed]);
  const trayTotal = sourceEdit?.heights.trayTypes.length ?? 0;
  // The suggestion's counts, not the live ones: the card stays in the thread and must read the same later.
  const detectedWalls = walls?.control.summary.detectedWalls;
  const detectedDoors = walls?.control.summary.detectedDoors;
  const wizard = useMemo(
    () =>
      karlWizardSteps(
        t,
        boardTotal,
        workingBoards,
        INSTALLATION_HEIGHT_KEYS.length,
        trayTotal,
        detectedWalls === undefined || detectedDoors === undefined
          ? undefined
          : { walls: detectedWalls, doors: detectedDoors },
      ),
    [t, boardTotal, workingBoards, trayTotal, detectedWalls, detectedDoors],
  );
  const [stepIndex, setStepIndex] = useState(0);
  const [openedStep, setOpenedStep] = useState<string | null>(null);
  const [stepDone, setStepDone] = useState<Record<string, boolean>>({});

  const boardsConfirmed = sourceEdit?.boardsConfirmed ?? false;
  const heightsConfirmed = sourceEdit?.heights.confirmed ?? false;
  const traysConfirmed = sourceEdit?.heights.traysConfirmed ?? false;
  const wallsConfirmed = walls ? walls.confirmed : true;

  const wizardState = useMemo(
    () => ({
      hasLoaded: Boolean(steps.overview),
      boardsConfirmed,
      heightsConfirmed,
      traysConfirmed,
      wallsConfirmed,
      stepDone,
    }),
    [steps.overview, boardsConfirmed, heightsConfirmed, traysConfirmed, wallsConfirmed, stepDone],
  );

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const reported = useRef<Set<string>>(new Set());
  const baselinedFor = useRef<string | null>(null);

  const submit = async (text: string): Promise<void> => {
    setDraft("");
    if (text.trim() !== "") {
      trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_KARL_CHAT_MESSAGE_SENT);
    }
    await send(text, "user");
  };

  // A placed source only changes the numbers on the client, so Karl is told
  // what moved and what it came to — he cannot see the edit otherwise. The
  // first run for a plan is a baseline, not news: edits come back from storage.
  useEffect(() => {
    if (!sourceEdit) return;
    const planKey = planName ?? "";
    if (baselinedFor.current !== planKey) {
      baselinedFor.current = planKey;
      reported.current = new Set(Object.keys(sourceEdit.placed));
      return;
    }
    for (const label of Object.keys(sourceEdit.placed)) {
      if (reported.current.has(label)) continue;
      reported.current.add(label);
      const source =
        steps.sources.find((entry) => entry.label === label)?.name ??
        sourceEdit.added[label]?.name ??
        gaps.find(
          (gap) => gap.ask.source && shortBoardLabel(gap.ask.source) === label,
        )?.ask.source ??
        label;
      void report(
        t("smartSurveyor.cablesBeta.eventPlacedSourceNoTotal", { source }),
        t("smartSurveyor.cablesBeta.eventPlacedSourceAsk", { source }),
      );
    }
    // send() changes each turn; only a new placement should report.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceEdit?.placed, planName]);

  // "New chat" restarts the walkthrough, not just the prose; the plan's own edits stay.
  restartQueuesRef.current = () => {
    boardsQueue.reset();
    heightsQueue.reset();
    traysQueue.reset();
  };
  const resetAll = useCallback(() => {
    // Walking again means asking again: the steps' answers live in the plan, not the queue.
    sourceEdit?.reopenBoards();
    boardsQueue.reset();
    heightsQueue.reset();
    traysQueue.reset();
    setStepDone({});
    setStepIndex(0);
    setOpenedStep(null);
    reset();
  }, [boardsQueue, heightsQueue, traysQueue, reset, sourceEdit]);

  const walkthroughFor = useRef<string | null>(null);
  useEffect(() => {
    const planKey = planName ?? "";
    if (walkthroughFor.current === planKey) return;
    walkthroughFor.current = planKey;
    setStepDone({});
    setStepIndex(0);
    setOpenedStep(null);
  }, [planName]);

  useEffect(() => {
    if (onProvideReset) onProvideReset(resetAll);
  }, [onProvideReset, resetAll]);

  useEffect(() => {
    if (onProvideNewChat) onProvideNewChat(reset);
  }, [onProvideNewChat, reset]);

  // A run asked about from the list is answered in prose: the circuits are
  // corrected board by board, not run by run.
  const askAbout = useCallback(
    (circuitId: string, source: KarlAskAboutSource) => {
      const route = steps.assignments
        .flatMap((assignment) => assignment.circuits)
        .find((circuit) => circuit.circuitId === circuitId);
      void send(
        t("smartSurveyor.cablesBeta.chatAskAboutRoute", {
          route: route?.circuitKey || circuitId,
        }),
        source,
      );
    },
    [steps.assignments, send, t],
  );
  useEffect(() => {
    if (onProvideAskAbout) onProvideAskAbout(askAbout);
  }, [onProvideAskAbout, askAbout]);

  const boardUnderReview =
    openedStep === "boards"
      ? (boardsQueue.view.ask?.subjectKey?.replace(/^zone:/, "") ?? null)
      : null;
  useEffect(() => {
    onBoardUnderReview?.(boardUnderReview);
  }, [onBoardUnderReview, boardUnderReview]);

  const rename = sourceEdit?.rename;
  const add = sourceEdit?.add;
  const addingName = sourceEdit?.addingName ?? null;
  const zoneCorners = sourceEdit?.zoneCorners ?? 0;
  const finishZone = sourceEdit?.finishZone;
  const feedsByLabel = sourceEdit?.boardFeeds.byLabel;
  const typesByCategory = sourceEdit?.boardFeeds.typesByCategory;
  const saveFeeds = sourceEdit?.setBoardFeeds;
  const boardControls = useMemo(
    () =>
      rename && add && finishZone
        ? {
            rename,
            add,
            addingName,
            zoneCorners,
            finishZone,
            ...(feedsByLabel && typesByCategory && saveFeeds
              ? { feeds: { byLabel: feedsByLabel, typesByCategory, save: saveFeeds } }
              : {}),
          }
        : undefined,
    [rename, add, addingName, zoneCorners, finishZone, feedsByLabel, typesByCategory, saveFeeds],
  );

  // Steps close themselves when the work lands, however it landed.
  // From the first step, always: none can be skipped or put off, so the walk stands on the first one not done.
  const nextStep = nextOpenStep(wizard, 0, wizardState);
  const isDismissed = nextStep === -1;
  const isWalkthroughDone =
    nextStep === -1 && Boolean(steps.overview);

  useEffect(() => {
    if (nextStep === -1) return;
    if (nextStep !== stepIndex) setStepIndex(nextStep);
  }, [nextStep, stepIndex]);

  // The card waits for Karl to finish speaking and then lands a beat later.
  const hasSpoken = messages.some((entry) => entry.role === "assistant");
  useEffect(() => {
    if (isDismissed || isSending || !hasSpoken || !steps.overview) return;
    const step = wizard[stepIndex];
    if (!step) return;
    const timer = setTimeout(() => addCard(step.key), CARD_DELAY_MS);
    return () => clearTimeout(timer);
  }, [
    wizard,
    stepIndex,
    isDismissed,
    isSending,
    hasSpoken,
    addCard,
    steps.overview,
  ]);

  // A step closes whenever its questions are done and it is not yet confirmed — not only at the
  // instant the last one is answered. Confirming the boards waits on a settled main board, and a
  // single try made at the wrong moment (the plan still measuring, or a walk finished before a
  // reload) left step 1 answered and never confirmed, with step 2 locked behind it for good.
  const confirmBoards = sourceEdit?.confirmBoards;
  const mainBoardStatus = sourceEdit?.mainBoard.status;
  const jumpToBoardAsk = boardsQueue.jumpTo;
  useEffect(() => {
    if (!boardsQueue.isFinished || boardsConfirmed) return;
    // The walk can say the main board was answered while the plan holds no answer — an Undo, or a
    // step back past it. The step only confirms on a main board, so he is taken back to that question.
    if (mainBoardStatus === "answered" || mainBoardStatus === "note") confirmBoards?.();
    else jumpToBoardAsk(MAIN_BOARD_ASK_ID);
  }, [boardsQueue.isFinished, boardsConfirmed, confirmBoards, mainBoardStatus, jumpToBoardAsk]);
  const confirmHeights = sourceEdit?.heights.confirm;
  useEffect(() => {
    if (heightsQueue.isFinished && boardsConfirmed && !heightsConfirmed) confirmHeights?.();
  }, [heightsQueue.isFinished, boardsConfirmed, heightsConfirmed, confirmHeights]);
  const confirmTrays = sourceEdit?.heights.confirmTrays;
  useEffect(() => {
    if (traysQueue.isFinished && heightsConfirmed && !traysConfirmed) confirmTrays?.();
  }, [traysQueue.isFinished, heightsConfirmed, traysConfirmed, confirmTrays]);

  const isWallsStepOpen = openedStep === "walls" && Boolean(walls) && !wallsConfirmed;
  const showWalls = walls?.show;
  const setIsCorrectingWalls = walls?.control.setIsCorrecting;
  useEffect(() => {
    if (isWallsStepOpen) showWalls?.();
    else setIsCorrectingWalls?.(false);
  }, [isWallsStepOpen, showWalls, setIsCorrectingWalls]);

  useEffect(() => {
    if (isWalkthroughDone) addCard(WALKTHROUGH_DONE);
  }, [isWalkthroughDone, addCard]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages, isSending]);

  // Also on isActive: while the panel is display:none scrollHeight is 0, and
  // sizing from that leaves an un-clickable, zero-height field once shown.
  useEffect(() => {
    const field = inputRef.current;
    if (!field || !isActive) return;
    field.style.height = "auto";
    field.style.height = `${Math.max(field.scrollHeight, 40)}px`;
  }, [draft, isActive]);

  if (!projectId) return null;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto">
        <div className="space-y-3.5 px-4 py-3.5">
          {messages.map((entry, index) =>
            entry.role === "card" && entry.content === WALKTHROUGH_DONE ? (
              // Only while it is true: a thread saved before the steps became mandatory may say it too early.
              !isWalkthroughDone ? null :
              // The last step hands him to the chat: a message in the thread, not a card to walk again.
              <div key={index} className="karl-rise flex max-w-full gap-2.5">
                <KarlMark />
                <div className="min-w-0 flex-1 whitespace-pre-wrap pt-0.5 text-[14.5px] leading-relaxed text-slate-800">
                  {t("smartSurveyor.cablesBeta.walkthroughDone")}
                </div>
              </div>
            ) : entry.role === "card" ? (
              (() => {
                const position = wizard.findIndex(
                  (step) => step.key === entry.content,
                );
                const step = wizard[position];
                if (!step) return null;
                // A card left in the thread for a step he has not reached yet would read out of order.
                if (!isDismissed && position > stepIndex) return null;
                const isLive = !isDismissed && position === stepIndex;
                return (
                  <div key={index} className="flex flex-col gap-1.5 pl-8">
                    <KarlWizardCard
                      step={step}
                      position={position + 1}
                      total={wizard.length}
                      isLive={isLive}
                      isDone={step.isDone(wizardState)}
                      isPast={position < stepIndex}
                      inFlight={isSending}
                      isLocked={step.isLocked(wizardState)}
                      onYes={() => {
                        trackEvent(
                          ANALYTICS_EVENTS.SMART_SURVEYOR_KARL_STEP_STARTED,
                          { step: step.key },
                        );
                        setOpenedStep(step.key);
                      }}
                    />
                    {openedStep === "boards" && step.key === "boards" && !step.isDone(wizardState) && (
                      <KarlAskQueue
                        view={boardsQueue.view}
                        isFinished={boardsQueue.isFinished}
                        onAnswer={boardsQueue.answer}
                        onRunTool={boardsQueue.runTool}
                        onBack={boardsQueue.back}
                        onForward={boardsQueue.forward}
                        onCancelArm={boardsQueue.cancelArm}
                        boardControls={boardControls}
                        plan={plan}
                        diagram={diagram}
                      />
                    )}
                    {openedStep === "heights" && step.key === "heights" && !step.isDone(wizardState) && (
                      <KarlAskQueue
                        view={heightsQueue.view}
                        isFinished={heightsQueue.isFinished}
                        onAnswer={heightsQueue.answer}
                        onRunTool={heightsQueue.runTool}
                        onBack={heightsQueue.back}
                        onForward={heightsQueue.forward}
                        onCancelArm={heightsQueue.cancelArm}
                        plan={plan}
                      />
                    )}
                    {openedStep === "trays" && step.key === "trays" && !step.isDone(wizardState) && (
                      <KarlAskQueue
                        view={traysQueue.view}
                        isFinished={traysQueue.isFinished}
                        onAnswer={traysQueue.answer}
                        onRunTool={traysQueue.runTool}
                        onBack={traysQueue.back}
                        onForward={traysQueue.forward}
                        onCancelArm={traysQueue.cancelArm}
                        plan={plan}
                      />
                    )}
                    {isWallsStepOpen && step.key === "walls" && walls && (
                      <KarlWallsReview
                        control={walls.control}
                        onConfirm={() => {
                          walls.control.setIsCorrecting(false);
                          walls.confirm();
                        }}
                      />
                    )}
                  </div>
                );
              })()
            ) : entry.role === "event" ? (
              <div
                key={index}
                className="karl-rise flex items-center gap-2 py-0.5 text-[11.5px] text-slate-400"
              >
                <span className="h-px flex-1 bg-slate-200" />
                <span className="whitespace-pre-wrap text-center">
                  {entry.content}
                </span>
                <span className="h-px flex-1 bg-slate-200" />
              </div>
            ) : entry.role === "user" ? (
              <div
                key={index}
                className="ml-auto max-w-[88%] whitespace-pre-wrap rounded-2xl rounded-tr-md bg-blue-600 px-3.5 py-2.5 text-[14px] leading-snug text-white"
              >
                {entry.content}
              </div>
            ) : (
              <div key={index} className="karl-rise flex max-w-full gap-2.5">
                <KarlMark />
                <div className="min-w-0 flex-1 whitespace-pre-wrap pt-0.5 text-[14.5px] leading-relaxed text-slate-800">
                  {entry.content}
                </div>
              </div>
            ),
          )}

          {/* After the steps, a board he adds is asked about here, on its own: step 1 stays done and the circuits stay up. */}
          {boardsConfirmed && !boardsQueue.isFinished && boardsQueue.view.ask && (
            <div data-testid="karl-board-follow-up" className="flex flex-col gap-1.5 pl-8">
              <div className="karl-rise rounded-xl border border-blue-200 bg-blue-50/60 px-3 py-2.5 text-[13px] leading-snug text-slate-800">
                {boardsQueue.view.ask.subject
                  ? t("smartSurveyor.cablesBeta.boardFollowUp", { board: boardsQueue.view.ask.subject })
                  : t("smartSurveyor.cablesBeta.boardFollowUpAny")}
              </div>
              <KarlAskQueue
                view={boardsQueue.view}
                isFinished={boardsQueue.isFinished}
                onAnswer={boardsQueue.answer}
                onRunTool={boardsQueue.runTool}
                onBack={boardsQueue.back}
                onForward={boardsQueue.forward}
                onCancelArm={boardsQueue.cancelArm}
                boardControls={boardControls}
                plan={plan}
                diagram={diagram}
              />
            </div>
          )}

          {isSending && (
            <Thinking label={t("smartSurveyor.cablesBeta.chatThinking")} />
          )}
        </div>
      </div>

      <div className="flex-none border-t border-slate-200 px-4 pb-3 pt-2.5">
        <div className="flex items-end gap-1.5">
          <textarea
            ref={inputRef}
            rows={1}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                if (draft.trim() && !isSending) void submit(draft);
              }
            }}
            placeholder={t("smartSurveyor.cablesBeta.chatPlaceholder")}
            className="max-h-48 min-h-[40px] min-w-0 flex-1 resize-none overflow-y-auto rounded-lg bg-slate-100 px-3 py-2.5 text-[14px] leading-snug text-slate-800 outline-none placeholder:text-slate-400 focus:ring-2 focus:ring-blue-400/50"
          />
          <button
            type="button"
            onClick={() => void submit(draft)}
            disabled={isSending || !draft.trim()}
            className="flex h-10 flex-none items-center gap-1.5 rounded-lg bg-blue-600 px-3.5 text-[13px] font-semibold text-white transition-opacity disabled:opacity-40"
          >
            <Send size={14} />
            {t("smartSurveyor.cablesBeta.chatSend")}
          </button>
        </div>
        <div className="mt-1.5 flex select-none items-center gap-1.5 px-0.5 text-[10.5px] text-slate-400">
          <kbd className="rounded border border-slate-200 bg-slate-100 px-1.5 py-px font-mono text-[9.5px] leading-none">
            ↵
          </kbd>
          {t("smartSurveyor.cablesBeta.chatHintSend")}
          <span className="opacity-40">·</span>
          <kbd className="rounded border border-slate-200 bg-slate-100 px-1.5 py-px font-mono text-[9.5px] leading-none">
            ⇧↵
          </kbd>
          {t("smartSurveyor.cablesBeta.chatHintNewline")}
        </div>
      </div>
    </div>
  );
};
